import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../utils/tokenStorage';
import './StaticPages.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = {
  envoy:   { label: 'Gold Envoy Membership',   price: '$20',   period: '6 months', desc: 'Introductory launch offer — first 100 applications only' },
  single:  { label: 'Single Member',           price: '$190',  period: '6 months', desc: 'Full HSS Single membership — browse, match, move in' },
  couples: { label: 'Couples Member',          price: '$290',  period: '6 months', desc: 'Full HSS Couples membership — browse, match, move in together' },
};

// PayPal button components — proper JSX (no dangerouslySetInnerHTML)
const PP_STYLE = { textAlign:'center', border:'none', borderRadius:'0.25rem', minWidth:'11.625rem', padding:'0 2rem', height:'2.625rem', fontWeight:'bold', backgroundColor:'#FFD140', color:'#000000', fontFamily:'"Helvetica Neue",Arial,sans-serif', fontSize:'1rem', lineHeight:'1.25rem', cursor:'pointer' };
const PPLogo = () => <section style={{fontSize:'0.75rem'}}>Powered by <img src="https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg" alt="paypal" style={{height:'0.875rem',verticalAlign:'middle'}} /></section>;

const PayPalSingleButton = () => (
  <form action="https://www.paypal.com/ncp/payment/SMYE7XRCN9YSL" method="post" target="_blank" style={{display:'inline-grid',justifyItems:'center',alignContent:'start',gap:'0.5rem'}}>
    <input type="submit" value="Pay with PayPal — Single $190" style={PP_STYLE} />
    <img src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg" alt="cards" />
    <PPLogo />
  </form>
);

const PayPalCouplesButton = () => (
  <form action="https://www.paypal.com/ncp/payment/68P4D9GW53UL2" method="post" target="_blank" style={{display:'inline-grid',justifyItems:'center',alignContent:'start',gap:'0.5rem'}}>
    <input type="submit" value="Pay with PayPal — Couples $290" style={PP_STYLE} />
    <img src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg" alt="cards" />
    <PPLogo />
  </form>
);

const PayPalEnvoyButton = () => (
  <form action="https://www.paypal.com/ncp/payment/PPSBL52TV7G6Q" method="post" target="_blank" style={{display:'inline-grid',justifyItems:'center',alignContent:'start',gap:'0.5rem'}}>
    <input type="submit" value="Pay with PayPal — Gold Envoy $20" style={PP_STYLE} />
    <img src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg" alt="cards" />
    <PPLogo />
  </form>
);

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const sessionId = searchParams.get('session_id');
  const provider  = searchParams.get('provider');
  const cancelled = searchParams.get('cancelled');
  const packageIdParam = searchParams.get('package');

  // Load the current user (if logged in) so we can tag the payment and auto-pick a package
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/auth/me`, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCurrentUser(res.data))
      .catch(() => {});
  }, []);

  // Auto-pick package based on membership class/type
  const packageId = packageIdParam || (
    (currentUser?.membership_class === 'gold_envoy' || currentUser?.membership_class === 'envoy') ? 'envoy'
    : (currentUser?.membership_class === 'vip' || currentUser?.membership_class === 'waived' || currentUser?.membership_class === 'govt_subsidised') ? 'envoy'
    : currentUser?.membership_type === 'couples' ? 'couples'
    : 'single'
  );

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [paid, setPaid]       = useState(false);
  const [polling, setPolling] = useState(false);

  const pkg = PACKAGES[packageId] || PACKAGES.single;

  // Poll Stripe status on return from checkout
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!sessionId || provider) return;
    let attempts = 0;
    setPolling(true);
    const interval = setInterval(async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setPaid(true);
          setPolling(false);
          clearInterval(interval);
          setTimeout(() => navigate('/dashboard'), 2500);
        } else if (data.status === 'expired' || attempts >= 8) {
          setPolling(false);
          clearInterval(interval);
        }
      } catch (e) { console.error("Payment poll error:", e); clearInterval(interval); setPolling(false); }
    }, 2000);
    return () => clearInterval(interval);
  }, [sessionId, provider]);

  const handleStripe = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${API}/payments/stripe/create-checkout`, {
        package_id: packageId,
        origin: window.location.origin,
        user_id: currentUser?.user_id || '',
        email: currentUser?.email || '',
      });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  if (paid) return (
    <div className="static-page">
      <div style={{ maxWidth: 560, margin: '80px auto', textAlign: 'center', padding: '40px', background: '#f0fdf4', borderRadius: 16, border: '2px solid #86efac' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ color: '#059669', marginBottom: 8 }}>Payment Received!</h2>
        <p style={{ color: '#4b5563', marginBottom: 24 }}>Thank you — your HSS membership payment has been received. Taking you to your dashboard…</p>
        <button className="btn btn-primary" onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard-btn">Go to Dashboard</button>
      </div>
    </div>
  );

  return (
    <div className="static-page">
      <header className="static-header">
        <div className="container">
          <div className="header-content">
            <img src="/logo.png" alt="House Sharing Seniors" className="logo-image" onClick={() => navigate('/')} />
          </div>
        </div>
      </header>

      <section className="page-hero" style={{ background: '#1a2332', padding: '32px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ color: 'white', marginBottom: 8 }}>Complete Your Membership Payment</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)' }}>{pkg.label} — {pkg.price} for {pkg.period}</p>
        </div>
      </section>

      <section className="page-content">
        <div className="container" style={{ maxWidth: 640 }}>

          {cancelled && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 24, color: '#b91c1c' }}>
              Payment cancelled — no charge was made. Please try again when ready.
            </div>
          )}

          {polling && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '12px 16px', marginBottom: 24, color: '#1d4ed8' }}>
              Checking payment status...
            </div>
          )}

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 24, color: '#b91c1c' }}>
              {error}
            </div>
          )}

          {/* Package summary */}
          <div style={{ background: '#f8fafb', borderRadius: 12, padding: '20px 24px', marginBottom: 28, border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#1a2332' }}>{pkg.label}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{pkg.desc}</div>
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#1a2332' }}>{pkg.price}</div>
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>Duration: {pkg.period} &nbsp;|&nbsp; Currency: AUD</div>
          </div>

          {/* Payment options */}
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a2332', marginBottom: 16 }}>Choose Payment Method</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Stripe */}
            <button
              onClick={handleStripe}
              disabled={loading}
              data-testid="pay-stripe"
              style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: '#635bff', color: 'white', border: 'none', borderRadius: 10, cursor: loading ? 'wait' : 'pointer', fontSize: 15, fontWeight: 700 }}>
              <span style={{ fontSize: 24 }}>💳</span>
              <div style={{ textAlign: 'left' }}>
                <div>Pay by Card — Stripe</div>
                <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.85 }}>Visa, Mastercard, Amex — secure checkout</div>
              </div>
            </button>

            {/* GoCardless removed */}

            {/* PayPal */}
            {packageId === 'envoy' ? (
              <div style={{ padding: '16px 20px', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10, fontWeight: 600 }}>Pay via PayPal — $20 Gold Envoy</div>
                <PayPalEnvoyButton />
              </div>
            ) : packageId === 'single' ? (
              <div style={{ padding: '16px 20px', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10, fontWeight: 600 }}>Pay via PayPal</div>
                <PayPalSingleButton />
              </div>
            ) : packageId === 'couples' ? (
              <div style={{ padding: '16px 20px', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 10 }}>
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10, fontWeight: 600 }}>Pay via PayPal</div>
                <PayPalCouplesButton />
              </div>
            ) : null}

          </div>

          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 20, textAlign: 'center' }}>
            Your account will be activated by our team once payment is confirmed. Questions? Email <a href="mailto:members@housesharingseniors.com.au" style={{ color: '#2563eb' }}>members@housesharingseniors.com.au</a>
          </p>

        </div>
      </section>
    </div>
  );
}
